import { useEffect, useState } from "react";
import { Volume2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { speak } from "@/lib/tts";
import { IDS } from "@/lib/testIds";
import { GAME_ITEMS, shuffle, randomFrom } from "@/lib/gameData";
import { GAME_FEEDBACK_MS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function newRound() {
  const correct = randomFrom(GAME_ITEMS);
  const distractors = shuffle(GAME_ITEMS.filter((i) => i.word !== correct.word)).slice(0, 3);
  const options = shuffle([correct, ...distractors]);
  return { correct, options };
}

export default function PictureToWord() {
  const [round, setRound] = useState(newRound);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState(null); // {correct, word}
  const [locked, setLocked] = useState(false);
  const { award } = useAuth();

  useEffect(() => {
    const t = setTimeout(() => speak("What is this?"), 200);
    return () => clearTimeout(t);
  }, [round]);

  const pick = (word) => {
    if (locked) return;
    setLocked(true);
    setAttempts((a) => a + 1);
    if (word === round.correct.word) {
      setScore((s) => s + 1);
      award(10, "picture");
      setFeedback({ correct: true, word });
      speak(`${word}. Correct!`);
      toast.success(`Yes — that's ${word}.`);
      setTimeout(() => {
        setFeedback(null);
        setLocked(false);
        setRound(newRound());
      }, GAME_FEEDBACK_MS);
    } else {
      setFeedback({ correct: false, word });
      toast.error(`Not quite — it's "${round.correct.word}".`);
      setTimeout(() => {
        setFeedback(null);
        setLocked(false);
      }, GAME_FEEDBACK_MS);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div
          data-testid={IDS.games.picture.score}
          className="inline-flex items-center gap-2 bg-rose-50 text-rose-700 px-4 py-2 rounded-full font-bold text-sm"
        >
          Score: {score} / {attempts}
        </div>
        <button
          data-testid={IDS.games.picture.next}
          onClick={() => setRound(newRound())}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New picture
        </button>
      </div>

      <div className="text-center bg-slate-50 rounded-3xl p-10 mb-6">
        <div className="text-8xl sm:text-9xl mb-2">{round.correct.emoji}</div>
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500">
          Pick the word that matches
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {round.options.map((opt, i) => {
          const isRight = feedback && feedback.correct && feedback.word === opt.word;
          const isWrong = feedback && !feedback.correct && feedback.word === opt.word;
          return (
            <button
              key={opt.word}
              data-testid={IDS.games.picture.option(i)}
              onClick={() => pick(opt.word)}
              className={`rounded-3xl border-2 py-5 sm:py-6 font-display text-3xl sm:text-4xl font-semibold transition-all
                ${isRight ? "bg-emerald-500 border-emerald-600 text-white phonx-letter-pop" : ""}
                ${isWrong ? "bg-rose-100 border-rose-300 text-rose-600 phonx-shake" : ""}
                ${!feedback ? "bg-white border-slate-200 text-slate-800 hover:border-rose-400 hover:text-rose-500 hover:-translate-y-1 shadow-sm hover:shadow-lg" : ""}
              `}
            >
              {opt.word}
            </button>
          );
        })}
      </div>
    </div>
  );
}
